/* eslint max-lines: ["error", {max: 365}] -- v1 + v2 recommended-group visibility helpers */
import { isAfter, isSameDay } from "date-fns";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { UniversalStatusCode } from "@/constants/User";
import type { RecommendedGroupClass } from "@/store/api/schedule/groupClasses";
import {
  getRecommendedGroupClassNoneOfTheseTimes,
  getRecommendedGroupClassRemindMeLaterDate,
} from "@/utils/recommendedGroupClass/storage.util";

type PatientRecommendation = {
  patientExternalId: string;
  recommendedGroupClass: RecommendedGroupClass;
};

type CandidatePatient = {
  patientExternalId: string;
  universalStatusCode?: UniversalStatusCode;
};

const AllowedStatusCodes = new Set<UniversalStatusCode>([
  UniversalStatusCode.InTreatment,
  UniversalStatusCode.Onboarding,
  UniversalStatusCode.Paused,
]);

const isAllowedStatusCode = (
  status?: UniversalStatusCode,
): status is UniversalStatusCode => {
  return !isNil(status) && AllowedStatusCodes.has(status);
};

type ShouldSkipFetchArgs = {
  candidatePatients: CandidatePatient[];
  isBasicInfoLoading: boolean;
  isFullProfileLoading: boolean;
  isFullProfileSuccess: boolean;
  isUserOnboarded: boolean;
};

const shouldSkipRecommendedGroupClassFetch = ({
  candidatePatients,
  isBasicInfoLoading,
  isFullProfileLoading,
  isFullProfileSuccess,
  isUserOnboarded,
}: ShouldSkipFetchArgs): boolean => {
  return (
    isBasicInfoLoading ||
    isFullProfileLoading ||
    !isFullProfileSuccess ||
    !isUserOnboarded ||
    isEmpty(candidatePatients)
  );
};

type GroupClassItem = {
  groupClassExternalId?: string;
  isUserRegistered?: boolean;
};

type IsRecommendationEligibleArgs = {
  appUserId?: string;
  groupClasses: GroupClassItem[];
  patientExternalId: string;
  recommendedGroupClass: RecommendedGroupClass;
  today: Date;
};

/**
 * IDs of group classes the user is registered for (from group classes endpoint).
 *
 * NOTE: Sensitive group classes and proxy users
 *
 * In v1 of the recommended group class modal, proxy users are only surfaced
 * support-only group classes via a curated mapping, so `isSensitive` registrations
 * are not expected to be exposed through this flow.
 *
 * If we ever:
 * - recommend patient-facing or otherwise sensitive classes to proxy users,
 * - move from the static mapping to dynamic recommendation generation, or
 * - reuse this util in other contexts,
 *
 * we should revisit this helper and downstream consumers to ensure we do not leak
 * sensitive registrations for a patient to a proxy user.
 */
const getRegisteredGroupClassExternalIds = (
  groupClasses: GroupClassItem[],
): string[] =>
  groupClasses
    .filter((item) => item.isUserRegistered === true)
    .map((item) => item.groupClassExternalId)
    .filter((id): id is string => !isNil(id));

const isBlockedByStorage = (args: {
  appUserId?: string;
  patientExternalId: string;
  today: Date;
}): boolean => {
  const { appUserId, patientExternalId, today } = args;
  const storageParams = { appUserId, patientExternalId };

  // permanent "none of these times" dismissal always blocks
  if (getRecommendedGroupClassNoneOfTheseTimes(storageParams)) return true;

  // "remind me later" blocks until the stored date has passed
  const remindMeLaterDate =
    getRecommendedGroupClassRemindMeLaterDate(storageParams);
  return (
    !isNil(remindMeLaterDate) &&
    (isSameDay(remindMeLaterDate, today) || isAfter(remindMeLaterDate, today))
  );
};

const getRecommendedInstanceIds = (
  instances: RecommendedGroupClass["instances"],
): string[] =>
  instances
    .map((inst) => inst.groupClassExternalId)
    .filter((id): id is string => !isNil(id));

const isRecommendationEligible = ({
  appUserId,
  groupClasses,
  patientExternalId,
  recommendedGroupClass,
  today,
}: IsRecommendationEligibleArgs): boolean => {
  const recommendedInstanceIds = getRecommendedInstanceIds(
    recommendedGroupClass.instances,
  );
  if (isEmpty(recommendedInstanceIds)) return false;

  const registeredIds = new Set(
    getRegisteredGroupClassExternalIds(groupClasses),
  );
  if (recommendedInstanceIds.some((id) => registeredIds.has(id))) return false;

  if (isBlockedByStorage({ appUserId, patientExternalId, today })) return false;

  return true;
};

// eslint-disable-next-line max-params
type TriggerRecommendedGroupClass = (
  args: { patientExternalId: string },
  preferCacheValue?: boolean,
) => { unwrap: () => Promise<RecommendedGroupClass | undefined> };

// eslint-disable-next-line max-params
type TriggerGroupClasses = (
  args: Record<string, never>,
  preferCacheValue?: boolean,
) => { unwrap: () => Promise<GroupClassItem[]> };

type BuildQueueArgs = {
  appUserId?: string;
  candidatePatients: CandidatePatient[];
  today: Date;
  triggerGroupClasses: TriggerGroupClasses;
  triggerRecommendedGroupClass: TriggerRecommendedGroupClass;
};

type BuildPatientArgs = {
  appUserId?: string;
  groupClasses: GroupClassItem[];
  patientExternalId: string;
  today: Date;
  triggerRecommendedGroupClass: TriggerRecommendedGroupClass;
};

const buildRecommendationForPatient = async ({
  appUserId,
  groupClasses,
  patientExternalId,
  today,
  triggerRecommendedGroupClass,
}: BuildPatientArgs): Promise<null | PatientRecommendation> => {
  try {
    const recommendedGroupClass = await triggerRecommendedGroupClass(
      { patientExternalId },
      true,
    ).unwrap();

    if (isNil(recommendedGroupClass)) return null;

    const isEligible = isRecommendationEligible({
      appUserId,
      groupClasses,
      patientExternalId,
      recommendedGroupClass,
      today,
    });

    if (!isEligible) return null;

    return { patientExternalId, recommendedGroupClass };
  } catch {
    // Per-patient errors are logged in the API layer; treat as ineligible
    return null;
  }
};

// ── Recommended groups (redesign) ────────────────────────────────────────────
// Adapts the recommended-groups response into the legacy PatientRecommendation shape so
// the queue store, modal, and handlers stay unchanged. The group's `groupId`/`isCore` ride
// along on the recommendedGroupClass so the modal can fetch group details + show the Core
// tag; each instance's groupClassExternalId is the groupSectionEventId, and the
// groupSectionId is carried for registration.

// eslint-disable-next-line max-params
type TriggerRecommendedGroups = (
  args: { patientExternalId: string },
  preferCacheValue?: boolean,
) => { unwrap: () => Promise<RecommendedGroup[] | undefined> };

const recommendedGroupToRecommendedGroupClass = (
  group: RecommendedGroup,
): RecommendedGroupClass => ({
  description: group.description ?? "",
  groupId: group.groupId,
  iconName: group.iconName ?? "",
  instances: group.instances.map((instance) => ({
    endTime: instance.endTime,
    groupClassExternalId: instance.groupSectionEventId,
    groupSectionId: instance.groupSectionId,
    startTime: instance.startTime,
  })),
  isCore: group.isCore,
  lengthInWeeks: group.lengthInWeeks ?? 0,
  title: group.title,
});

// a recommended group is eligible when it has upcoming instances and is not
// snoozed/dismissed. Registration is checked once across all groups (see below), not here.
const isRecommendedGroupEligible = (args: {
  appUserId?: string;
  group: RecommendedGroup;
  patientExternalId: string;
  today: Date;
}): boolean => {
  const { appUserId, group, patientExternalId, today } = args;
  if (isEmpty(group.instances)) return false;
  if (isBlockedByStorage({ appUserId, patientExternalId, today })) return false;
  return true;
};

// the patient is already engaged with groups if they are registered for ANY recommended
// group — in that case we do not recommend anything (mirrors v1, where a single
// registration suppresses the modal). Registration is now a group-level flag on the
// recommended-groups response (previously derived from per-instance flags).
const isRegisteredForAnyGroup = (groups: RecommendedGroup[]): boolean =>
  groups.some((group) => group.isRegistered);

const buildRecommendedGroupForPatient = async (args: {
  appUserId?: string;
  patientExternalId: string;
  today: Date;
  triggerRecommendedGroups: TriggerRecommendedGroups;
}): Promise<null | PatientRecommendation> => {
  const { appUserId, patientExternalId, today, triggerRecommendedGroups } =
    args;
  try {
    const groups =
      (await triggerRecommendedGroups({ patientExternalId }, true).unwrap()) ??
      [];
    if (isRegisteredForAnyGroup(groups)) return null;

    const eligibleGroup = groups.find((group) =>
      isRecommendedGroupEligible({
        appUserId,
        group,
        patientExternalId,
        today,
      }),
    );
    if (isNil(eligibleGroup)) return null;
    return {
      patientExternalId,
      recommendedGroupClass:
        recommendedGroupToRecommendedGroupClass(eligibleGroup),
    };
  } catch {
    return null;
  }
};

// resolves one recommendation per candidate patient, then drops the ineligible
// (null) ones — shared by the v1 and v2 queue builders.
const resolveRecommendationQueue = async ({
  buildForPatient,
  candidatePatients,
}: {
  buildForPatient: (
    patientExternalId: string,
  ) => Promise<null | PatientRecommendation>;
  candidatePatients: CandidatePatient[];
}): Promise<PatientRecommendation[]> => {
  const results = await Promise.all(
    candidatePatients.map(({ patientExternalId }) =>
      buildForPatient(patientExternalId),
    ),
  );
  return results.filter(
    (recommendation): recommendation is PatientRecommendation =>
      !isNil(recommendation),
  );
};

const buildRecommendedGroupsQueue = ({
  appUserId,
  candidatePatients,
  today,
  triggerRecommendedGroups,
}: {
  appUserId?: string;
  candidatePatients: CandidatePatient[];
  today: Date;
  triggerRecommendedGroups: TriggerRecommendedGroups;
}): Promise<PatientRecommendation[]> =>
  resolveRecommendationQueue({
    buildForPatient: (patientExternalId) =>
      buildRecommendedGroupForPatient({
        appUserId,
        patientExternalId,
        today,
        triggerRecommendedGroups,
      }),
    candidatePatients,
  });

const buildRecommendationsQueue = async ({
  appUserId,
  candidatePatients,
  today,
  triggerGroupClasses,
  triggerRecommendedGroupClass,
}: BuildQueueArgs): Promise<PatientRecommendation[]> => {
  const groupClasses = await triggerGroupClasses({}, true).unwrap();

  return resolveRecommendationQueue({
    buildForPatient: (patientExternalId) =>
      buildRecommendationForPatient({
        appUserId,
        groupClasses,
        patientExternalId,
        today,
        triggerRecommendedGroupClass,
      }),
    candidatePatients,
  });
};

export {
  buildRecommendationsQueue,
  buildRecommendedGroupsQueue,
  isAllowedStatusCode,
  isRecommendationEligible,
  shouldSkipRecommendedGroupClassFetch,
};
export type { CandidatePatient, PatientRecommendation };
